import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAppPath } from '../hooks/useAppPath'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Search,
  SlidersHorizontal,
  X,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  Building2,
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { ListingCard } from '../components/marketplace/ListingCard'
import { MarketplaceHero } from '../components/marketplace/MarketplaceHero'
import { ComplianceFooter } from '../components/marketplace/ComplianceFooter'
import { ListingDetailView } from '../components/marketplace/ListingDetail'
import { marketplaceApi } from '../services/api/marketplace'
import { DstListing } from '../services/api/admin'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../components/ui/Dialog'

const ASSET_CLASSES = [
  'multifamily',
  'industrial',
  'office',
  'retail',
  'healthcare',
  'self-storage',
  'other',
] as const

const PAGE_SIZE = 24

const filterSchema = z.object({
  search: z.string(),
  sponsor: z.string(),
  assetClasses: z.array(z.string()),
  minReturn: z.number().min(0).max(100).nullable(),
  maxReturn: z.number().min(0).max(100).nullable(),
  page: z.number().int().positive(),
})

type FilterValues = z.infer<typeof filterSchema>

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-muted" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="grid grid-cols-3 gap-3">
          <div className="h-10 bg-muted rounded" />
          <div className="h-10 bg-muted rounded" />
          <div className="h-10 bg-muted rounded" />
        </div>
        <div className="h-9 bg-muted rounded-md mt-4" />
      </div>
    </div>
  )
}

interface ListingOverlayProps {
  listingId: string
  allListings: DstListing[]
  locationState: unknown
}

function ListingOverlay({ listingId, allListings, locationState }: ListingOverlayProps) {
  const navigate = useNavigate()
  const appPath = useAppPath()

  const cachedListing = useMemo(
    () => allListings.find((l) => l.id === listingId) ?? null,
    [allListings, listingId]
  )

  const stateHasListing =
    locationState !== null &&
    typeof locationState === 'object' &&
    'listing' in (locationState as Record<string, unknown>)

  const stateListing: DstListing | null = stateHasListing
    ? ((locationState as { listing: DstListing }).listing)
    : null

  const preloadedListing = stateListing ?? cachedListing

  const { data: fetchedListing, status: fetchStatus } = useQuery({
    queryKey: ['marketplace', 'listing', listingId],
    queryFn: () => marketplaceApi.getListing(listingId),
    enabled: preloadedListing == null,
    retry: false,
  })

  const listing = preloadedListing ?? fetchedListing ?? null

  function handleClose() {
    navigate(appPath('/marketplace'))
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <DialogContent
        className={[
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-5xl p-0 border-0',
          'bg-background/95 backdrop-blur-md',
          'rounded-3xl shadow-2xl',
          'max-h-[90vh] overflow-y-auto',
          'data-[state=open]:animate-in data-[state=open]:zoom-in-95 data-[state=open]:fade-in',
          'data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out',
          'duration-200',
          'z-[100]',
        ].join(' ')}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">
          {listing ? listing.title : 'Listing details'}
        </DialogTitle>

        <div className="px-4 pt-4 pb-2 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="gap-2"
            aria-label="Back to Marketplace"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to Marketplace
          </Button>
        </div>

        {preloadedListing == null && fetchStatus === 'pending' && (
          <div
            className="space-y-4 p-6"
            aria-label="Loading listing"
          >
            <div className="aspect-video bg-muted animate-pulse rounded-2xl" />
            <div className="h-8 bg-muted animate-pulse rounded-lg w-2/3" />
            <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
            <div className="h-48 bg-muted animate-pulse rounded-[28px]" />
          </div>
        )}

        {preloadedListing == null && fetchStatus === 'error' && (
          <div className="p-6">
            <Card className="p-12 text-center">
              <Building2
                className="h-16 w-16 text-muted-foreground mx-auto mb-4"
                aria-hidden="true"
              />
              <h2 className="text-lg font-semibold text-foreground mb-2">Listing not found</h2>
              <p className="text-muted-foreground mb-4">
                This listing may have been removed or is no longer available.
              </p>
              <Button onClick={handleClose}>Back to Marketplace</Button>
            </Card>
          </div>
        )}

        {listing && <ListingDetailView listing={listing} />}
      </DialogContent>
    </Dialog>
  )
}

export function MarketplacePage() {
  const { id: overlayListingId } = useParams<{ id?: string }>()
  const location = useLocation()
  const { control, watch, setValue, reset } = useForm<FilterValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      search: '',
      sponsor: '',
      assetClasses: [],
      minReturn: null,
      maxReturn: null,
      page: 1,
    },
  })

  const filterValues = watch()
  const [submittedFilters, setSubmittedFilters] = useState<FilterValues>(filterValues)

  const serverFilters = useMemo(() => ({
    sponsor: submittedFilters.sponsor || undefined,
    asset_class: submittedFilters.assetClasses.length === 1
      ? submittedFilters.assetClasses[0]
      : undefined,
    min_return: submittedFilters.minReturn ?? undefined,
    max_return: submittedFilters.maxReturn ?? undefined,
    page: submittedFilters.page,
    limit: PAGE_SIZE,
  }), [submittedFilters])

  const { data, status, error, refetch } = useQuery({
    queryKey: ['marketplace', 'listings', serverFilters],
    queryFn: () => marketplaceApi.listListings(serverFilters),
    placeholderData: (previousData) => previousData,
  })

  const listings = useMemo(() => {
    if (!data?.listings) return []
    const searchLower = submittedFilters.search.toLowerCase()
    if (!searchLower) return data.listings
    return data.listings.filter((listing) =>
      listing.title.toLowerCase().includes(searchLower) ||
      (listing.sponsor?.toLowerCase().includes(searchLower) ?? false) ||
      (listing.addressCity?.toLowerCase().includes(searchLower) ?? false)
    )
  }, [data?.listings, submittedFilters.search])

  const total = data?.total ?? 0
  const currentPage = submittedFilters.page
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const featuredListing = listings[0] ?? null

  const distinctSponsors = useMemo(() => {
    if (!data?.listings) return []
    const sponsors = data.listings
      .map((l) => l.sponsor)
      .filter((s): s is string => s != null && s.length > 0)
    return [...new Set(sponsors)].sort()
  }, [data?.listings])

  function applyFilters() {
    setSubmittedFilters({ ...filterValues, page: 1 })
  }

  function clearFilters() {
    reset()
    setSubmittedFilters({
      search: '',
      sponsor: '',
      assetClasses: [],
      minReturn: null,
      maxReturn: null,
      page: 1,
    })
  }

  function toggleAssetClass(cls: string) {
    const current = filterValues.assetClasses
    const next = current.includes(cls)
      ? current.filter((c) => c !== cls)
      : [...current, cls]
    setValue('assetClasses', next)
  }

  function goToPage(page: number) {
    setSubmittedFilters((prev) => ({ ...prev, page }))
  }

  const hasActiveFilters =
    submittedFilters.search.length > 0 ||
    submittedFilters.sponsor.length > 0 ||
    submittedFilters.assetClasses.length > 0 ||
    submittedFilters.minReturn != null ||
    submittedFilters.maxReturn != null

  const gridListings = featuredListing ? listings.slice(1) : listings

  return (
    <div className="flex flex-col gap-0 -mx-4 sm:-mx-6 lg:-mx-8">
      {status === 'success' && featuredListing && (
        <div className="px-4 sm:px-6 lg:px-8 pb-6">
          <MarketplaceHero listing={featuredListing} />
        </div>
      )}

      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 sm:px-6 lg:px-8 py-3">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Controller
                name="search"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Search by title, sponsor, or city…"
                    className="pl-10"
                    aria-label="Search listings"
                  />
                )}
              />
            </div>
            <Controller
              name="sponsor"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Filter by sponsor"
                >
                  <option value="">All Sponsors</option>
                  {distinctSponsors.map((sponsor) => (
                    <option key={sponsor} value={sponsor}>
                      {sponsor}
                    </option>
                  ))}
                </select>
              )}
            />
            <Button
              type="button"
              onClick={applyFilters}
            >
              Apply Filters
            </Button>
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                onClick={clearFilters}
                className="gap-1.5"
              >
                <X className="h-4 w-4" aria-hidden="true" />
                Clear Filters
              </Button>
            )}
            {data && (
              <span className="text-sm text-muted-foreground ml-auto">
                {total.toLocaleString()} listing{total !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
              Asset Class:
            </span>
            {ASSET_CLASSES.map((cls) => {
              const active = filterValues.assetClasses.includes(cls)
              return (
                <button
                  key={cls}
                  type="button"
                  onClick={() => toggleAssetClass(cls)}
                  aria-pressed={active}
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border transition-colors capitalize ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-foreground hover:border-primary hover:text-primary'
                  }`}
                >
                  {cls}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto w-full">
        {status === 'pending' && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            aria-label="Loading listings"
            aria-busy="true"
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        )}

        {status === 'error' && (
          <Card className="p-10 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Unable to load listings</h3>
            <p className="text-muted-foreground mb-4">
              {(error as Error)?.message ?? 'Something went wrong. Please try again.'}
            </p>
            <Button
              onClick={() => refetch()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Retry
            </Button>
          </Card>
        )}

        {status === 'success' && listings.length === 0 && (
          <Card className="p-12 text-center">
            <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No listings match your filters
            </h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search criteria or clearing your filters.
            </p>
            <Button onClick={clearFilters}>Clear Filters</Button>
          </Card>
        )}

        {status === 'success' && listings.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gridListings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <nav
                className="flex items-center justify-center gap-2 mt-8"
                aria-label="Pagination"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  Next
                </Button>
              </nav>
            )}
          </>
        )}
      </div>

      <ComplianceFooter />

      {overlayListingId && (
        <ListingOverlay
          listingId={overlayListingId}
          allListings={data?.listings ?? []}
          locationState={location.state}
        />
      )}
    </div>
  )
}
